import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import './FacilityForm.css';

const FacilityForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    icon: '',
    category: 'general',
    isActive: true,
    displayOrder: 0
  });

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'room', label: 'Room' },
    { value: 'dining', label: 'Dining' },
    { value: 'recreation', label: 'Recreation' },
    { value: 'business', label: 'Business' },
    { value: 'transport', label: 'Transport' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (isEdit) {
      fetchFacility();
    }
  }, [id]);

  const fetchFacility = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/facilities/${id}`);
      const facility = response.data.data;
      setFormData({
        name: facility.name || '',
        icon: facility.icon || '',
        category: facility.category || 'general',
        isActive: facility.isActive !== undefined ? facility.isActive : true,
        displayOrder: facility.displayOrder || 0
      });
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch facility');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Facility name is required');
      return;
    }

    try {
      if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/facilities/${id}`, formData);
      } else {
        await axios.post(`${API_BASE_URL}/admin/facilities`, formData);
      }
      navigate('/sy-admin/facilities');
    } catch (error) {
      setError(error.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} facility`);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="facility-form-container">
          <div className="facility-form-header">
            <div className="skeleton skeleton-text" style={{ width: '250px', height: '40px', borderRadius: '8px' }}></div>
            <div className="skeleton skeleton-button" style={{ width: '180px', height: '40px', borderRadius: '8px' }}></div>
          </div>
          <div className="facility-form-card">
            <div className="form-section">
              <div className="skeleton skeleton-text" style={{ width: '200px', height: '32px', borderRadius: '8px', marginBottom: '24px' }}></div>
              <div className="form-row">
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
              </div>
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '60px', height: '18px', borderRadius: '4px' }}></div>
              </div>
            </div>
            <div className="form-actions">
              <div className="skeleton skeleton-button" style={{ width: '150px', height: '44px', borderRadius: '8px' }}></div>
              <div className="skeleton skeleton-button" style={{ width: '100px', height: '44px', borderRadius: '8px' }}></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="facility-form-container">
        <div className="facility-form-header">
          <h1>{isEdit ? 'Edit Facility' : 'Add New Facility'}</h1>
          <button
            onClick={() => navigate('/sy-admin/facilities')}
            className="btn btn-secondary"
          >
            ← Back to Facilities
          </button>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="facility-form-card">
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h2>Basic Information</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Facility Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Free WiFi, Parking"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="icon">Icon (Emoji/Text)</label>
                  <input
                    type="text"
                    id="icon"
                    name="icon"
                    value={formData.icon}
                    onChange={handleChange}
                    placeholder="e.g., 📶, 🅿️, or WiFi"
                    maxLength={10}
                  />
                  <small>Optional: Add an emoji or text icon</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="displayOrder">Display Order</label>
                  <input
                    type="number"
                    id="displayOrder"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleChange}
                    min="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                  <span>Active</span>
                </label>
                <small>Inactive facilities won't be available for selection</small>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {isEdit ? 'Update Facility' : 'Create Facility'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/sy-admin/facilities')}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default FacilityForm;
